import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { GlobalWebSocketProvider } from '@/contexts/GlobalWebSocketContext';
import { WalletProvider } from '@/contexts/WalletContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { DesignProvider } from '@/contexts/DesignProvider';
import { saveAffiliateRef } from '@/lib/affiliate';
import { OnboardingProvider } from '@/contexts/OnboardingProvider';
import { useDOI } from '@/contexts/DOIContext';
import { DOIProvider } from '@/contexts/DOIProvider';
import { ServiceWorkerProvider } from '@/components/ServiceWorkerProvider';
import { AdminSidebarProvider } from '@/contexts/AdminSidebarProvider';
import { useEditMode } from '@/contexts/EditModeContext';
import { EditModeProvider } from '@/contexts/EditModeProvider';
const DOIVerificationModal = React.lazy(() => import('@/components/verification/DOIVerificationModal'));
const Navigation = React.lazy(() =>
  import('@/components/layout/Navigation').then((module) => ({ default: module.Navigation }))
);
const MobileHeader = React.lazy(() =>
  import('@/components/layout/MobileHeader').then((module) => ({ default: module.MobileHeader }))
);
const AdminFloatingButton = React.lazy(() =>
  import('@/components/layout/AdminFloatingButton').then((module) => ({ default: module.AdminFloatingButton }))
);
const Index = React.lazy(() => import('@/pages/Index'));
const Characters = React.lazy(() => import('@/pages/Characters'));
const CharacterProfile = React.lazy(() => import('@/pages/CharacterProfile'));
const CharacterLandingPage = React.lazy(() => import('@/pages/CharacterLandingPage'));
const MagicLinkLandingPage = React.lazy(() => import('@/pages/MagicLinkLandingPage'));
const AffiliateInvitationPage = React.lazy(() => import('@/pages/AffiliateInvitationPage'));
const ChatOverview = React.lazy(() => import('@/pages/ChatOverview'));
const Auth = React.lazy(() => import('@/pages/Auth'));
const Dashboard = React.lazy(() => import('@/pages/Dashboard'));
const DailyChallenges = React.lazy(() => import('@/pages/DailyChallenges'));
const Profile = React.lazy(() => import('@/pages/Profile'));
const PartnerBillingDashboard = React.lazy(() => import('@/pages/PartnerBillingDashboard'));
import { ProtectedRoute } from '@/components/ProtectedRoute';
// removed unused AdminLayout and Toaster imports
import { ToastProvider } from '@/components/notifications/ToastProvider';
import { useStickyHeaderContent } from '@/contexts/StickyHeaderContext';
import { StickyHeaderProvider } from '@/contexts/StickyHeaderProvider';
import { cn } from '@/lib/utils';
import { ScrollToTop } from '@/components/layout/ScrollToTop';
const LocationSettings = React.lazy(() => import('@/pages/LocationSettings'));
const Wallet = React.lazy(() => import('@/pages/Wallet'));
const Shop = React.lazy(() => import('@/pages/Shop'));
const Swipe = React.lazy(() => import('@/pages/Swipe'));
const Matches = React.lazy(() => import('@/pages/Matches'));
const Favorites = React.lazy(() => import('@/pages/Favorites'));
const Support = React.lazy(() => import('@/pages/Support'));
const NotFound = React.lazy(() => import('@/pages/NotFound'));
import { LoadingBar } from '@/components/ui/loading-bar';
import { PageViewTracker } from '@/components/analytics/PageViewTracker';
import { AnonymousTrackingProvider } from '@/components/analytics/AnonymousTrackingProvider';
const EmailVerification = React.lazy(() => import('@/pages/EmailVerification'));
const ResetPasswordRequest = React.lazy(() => import('@/pages/ResetPasswordRequest'));
const ResetPassword = React.lazy(() => import('@/pages/ResetPassword'));
const NotificationCenter = React.lazy(() => import('@/pages/NotificationCenter'));
const NotificationSettings = React.lazy(() => import('@/pages/NotificationSettings'));
const AchievementDashboard = React.lazy(() => import('@/pages/AchievementDashboard'));
const ProfileVisits = React.lazy(() => import('@/pages/ProfileVisits'));
const CreatorSpace = React.lazy(() => import('@/pages/CreatorSpace'));
const CreatorProfile = React.lazy(() => import('@/pages/CreatorProfile'));
const BillingResultSuccess = React.lazy(() => import('@/pages/BillingResultSuccess'));
const BillingResultCancel = React.lazy(() => import('@/pages/BillingResultCancel'));
const BillingPending = React.lazy(() => import('@/pages/BillingPending'));
const Unsubscribed = React.lazy(() => import('@/pages/Unsubscribed'));
const CancelContract = React.lazy(() => import('@/pages/CancelContract'));
const PaymentResultHandler = React.lazy(() => import('@/components/billing/PaymentResultHandler'));
const SupportBubble = React.lazy(() =>
  import('@/components/support/SupportBubble').then((module) => ({ default: module.SupportBubble }))
);
const Footer = React.lazy(() => import('@/components/layout/Footer').then((module) => ({ default: module.Footer })));
const InlineEditHintBar = React.lazy(() =>
  import('@/components/i18n/InlineEditHintBar').then((module) => ({ default: module.InlineEditHintBar }))
);
const GlobalTextEditor = React.lazy(() =>
  import('@/components/i18n/GlobalTextEditor').then((module) => ({ default: module.GlobalTextEditor }))
);
const ClickToEditListener = React.lazy(() =>
  import('@/components/i18n/ClickToEditListener').then((module) => ({ default: module.ClickToEditListener }))
);
import { useMediaQuery } from '@/hooks/useMediaQuery';
// import { InlineEditOverlayScanner } from '@/components/i18n/InlineEditOverlayScanner';
const LegalImpressum = React.lazy(() => import('@/pages/LegalImpressum'));
const LegalDatenschutz = React.lazy(() => import('@/pages/LegalDatenschutz'));
const LegalAGB = React.lazy(() => import('@/pages/LegalAGB'));
const MySubscription = React.lazy(() => import('@/pages/MySubscription'));
const AdminRouteMount = React.lazy(() => import('@/routes/AdminRouteMount'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 60_000, // 1 minute default
      gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
    },
  },
});

/**
 * Inner component that renders the sticky header from context.
 * Must be inside StickyHeaderProvider to access the context.
 *
 * Uses position: fixed (like StickyMiniGallery on CharacterProfile) because
 * position: sticky doesn't work reliably in nested flex containers.
 * Fixed positioning is viewport-relative and works independently of scroll hierarchy.
 */
function StickyHeaderRenderer(): React.ReactElement | null {
  const stickyHeader = useStickyHeaderContent();

  if (!stickyHeader) return null;

  return (
    <>
      {/* Fixed header bar */}
      <div
        className={cn(
          'app-sticky-header',
          // Fixed position - directly under the fixed Navigation (same approach as StickyMiniGallery)
          'fixed left-0 right-0 z-40',
          // Mobile: top-0, Desktop: directly below the template-specific navigation height
          'top-0 md:top-[var(--nav-height-desktop,4rem)]',
          // Glaseffekt (consistent with design system)
          'bg-white/5 backdrop-blur-xl border-b border-white/10',
          // Padding
          'py-token-sm sm:py-token-md px-token-md sm:px-token-lg'
        )}
      >
        <div className="w-full max-w-screen-xl mx-auto">{stickyHeader}</div>
      </div>
      {/* Spacer to push content below the fixed header */}
      <div className="sticky-header-spacer h-[120px] sm:h-[140px]" />
    </>
  );
}

function AffiliateRefCapture(): null {
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      saveAffiliateRef(ref);
    }
  }, [searchParams]);
  return null;
}

function DesktopNavigationFallback(): React.ReactElement {
  return (
    <div
      className="fixed inset-x-0 top-0 z-50 h-[calc(var(--nav-height-desktop)+1px)] border-b border-white/10 bg-background/80 backdrop-blur-xl"
      aria-hidden="true"
    />
  );
}

function MobileNavigationFallback(): React.ReactElement {
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 h-[calc(var(--nav-height-mobile)+env(safe-area-inset-bottom,0px)+1px)] border-t border-white/10 bg-background/95 backdrop-blur-xl md:hidden"
      aria-hidden="true"
    />
  );
}

function FooterFallback(): React.ReactElement {
  return <div className="hidden h-[4.5rem] border-t border-white/10 md:block" aria-hidden="true" />;
}

function EditChrome(): React.ReactElement | null {
  const { isEditMode } = useEditMode();
  if (!isEditMode) return null;

  return (
    <React.Suspense fallback={null}>
      <InlineEditHintBar />
      <GlobalTextEditor />
      <ClickToEditListener />
    </React.Suspense>
  );
}

function DOIModalMount(): React.ReactElement | null {
  const { isModalOpen } = useDOI();
  if (!isModalOpen) return null;

  return (
    <React.Suspense fallback={null}>
      <DOIVerificationModal />
    </React.Suspense>
  );
}

function PaymentResultMount(): React.ReactElement {
  return (
    <React.Suspense fallback={null}>
      <PaymentResultHandler />
    </React.Suspense>
  );
}

function UserRouteFrame({ children }: { children: React.ReactNode }): React.ReactElement {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'root_admin' || user?.role === 'demo_admin';

  return (
    <>
      {isDesktop && (
        <div className="sticky top-0 z-50 w-full">
          <React.Suspense fallback={<DesktopNavigationFallback />}>
            <Navigation />
          </React.Suspense>
        </div>
      )}
      {isAdmin && (
        <React.Suspense fallback={null}>
          <AdminFloatingButton />
        </React.Suspense>
      )}
      {isDesktop && (
        <React.Suspense fallback={null}>
          <SupportBubble hideForAdmins />
        </React.Suspense>
      )}

      <EditChrome />

      <StickyHeaderProvider>
        <main className="app-main">
          <StickyHeaderRenderer />
          {children}
        </main>
      </StickyHeaderProvider>

      {isDesktop && (
        <React.Suspense fallback={<FooterFallback />}>
          <Footer />
        </React.Suspense>
      )}
      {!isDesktop && (
        <React.Suspense fallback={<MobileNavigationFallback />}>
          <MobileHeader />
        </React.Suspense>
      )}
    </>
  );
}

function AdminAreaFloatingButton(): React.ReactElement | null {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'root_admin' || user?.role === 'demo_admin';

  if (!isAdmin) {
    return null;
  }

  return (
    <React.Suspense fallback={null}>
      <AdminFloatingButton />
    </React.Suspense>
  );
}

function RouteChrome({ children }: { children: React.ReactNode }): React.ReactElement {
  const { pathname } = useLocation();
  const isAdminRoute = pathname === '/admin' || pathname.startsWith('/admin/');

  return (
    <div className="app-wrapper design-page-gradient" data-app-shell={isAdminRoute ? 'admin' : 'user'}>
      <ScrollToTop />
      <LoadingBar />
      <AffiliateRefCapture />
      <PageViewTracker />
      <AnonymousTrackingProvider />

      {isAdminRoute ? (
        <>
          <EditChrome />
          <AdminAreaFloatingButton />
          {children}
        </>
      ) : (
        <UserRouteFrame>{children}</UserRouteFrame>
      )}

      <PaymentResultMount />
    </div>
  );
}

function App(): React.ReactElement {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ServiceWorkerProvider>
          <DesignProvider>
            <AuthProvider>
              <EditModeProvider>
                <GlobalWebSocketProvider>
                  <NotificationProvider>
                    <ToastProvider>
                      <WalletProvider>
                        <DOIProvider>
                          <OnboardingProvider>
                            <DOIModalMount />
                            <AdminSidebarProvider>
                              <RouteChrome>
                                <React.Suspense
                                  fallback={<div className="p-token-md text-sm text-muted-foreground">Laden…</div>}
                                >
                                  <Routes>
                                    {/* Billing redirect endpoints */}
                                    <Route path="/billing/success" element={<BillingResultSuccess />} />
                                    <Route path="/billing/cancel" element={<BillingResultCancel />} />
                                    <Route path="/billing/pending" element={<BillingPending />} />
                                    <Route path="/unsubscribed" element={<Unsubscribed />} />
                                    <Route path="/kuendigen" element={<CancelContract />} />
                                    {/* Public Legal Pages */}
                                    <Route path="/impressum" element={<LegalImpressum />} />
                                    <Route path="/datenschutz" element={<LegalDatenschutz />} />
                                    <Route path="/agb" element={<LegalAGB />} />
                                    <Route path="/" element={<Index />} />
                                    <Route path="/characters" element={<Characters />} />
                                    <Route path="/c/:slug" element={<CharacterLandingPage />} />
                                    <Route path="/msg/:slug" element={<MagicLinkLandingPage />} />
                                    <Route path="/invite/:publicId" element={<AffiliateInvitationPage />} />
                                    <Route path="/characters/:id" element={<CharacterProfile />} />
                                    {/* Chat Routes - optionaler characterId Parameter für persistentes Layout */}
                                    <Route
                                      path="/chats/:characterId?"
                                      element={
                                        <ProtectedRoute>
                                          <ChatOverview />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/chat/:characterId"
                                      element={
                                        <ProtectedRoute>
                                          <ChatOverview />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route path="/auth" element={<Auth />} />
                                    <Route path="/verify-email" element={<EmailVerification />} />
                                    <Route path="/forgot-password" element={<ResetPasswordRequest />} />
                                    <Route path="/reset-password" element={<ResetPassword />} />
                                    <Route
                                      path="/dashboard"
                                      element={
                                        <ProtectedRoute>
                                          <Dashboard />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/daily-challenges"
                                      element={
                                        <ProtectedRoute>
                                          <DailyChallenges />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/profile"
                                      element={
                                        <ProtectedRoute>
                                          <Profile />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/profile/visits"
                                      element={
                                        <ProtectedRoute>
                                          <ProfileVisits />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/settings/location"
                                      element={
                                        <ProtectedRoute>
                                          <LocationSettings />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/wallet"
                                      element={
                                        <ProtectedRoute>
                                          <Wallet />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/shop"
                                      element={
                                        <ProtectedRoute>
                                          <Shop />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/notifications"
                                      element={
                                        <ProtectedRoute>
                                          <NotificationCenter />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/notifications/settings"
                                      element={
                                        <ProtectedRoute>
                                          <NotificationSettings />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/achievements"
                                      element={
                                        <ProtectedRoute>
                                          <AchievementDashboard />
                                        </ProtectedRoute>
                                      }
                                    />

                                    {/* Creator Space Routes */}
                                    <Route
                                      path="/creator-space"
                                      element={
                                        <ProtectedRoute>
                                          <CreatorSpace />
                                        </ProtectedRoute>
                                      }
                                    />
                                    {/* Redirect old /my-subscriptions to new unified page with creators tab */}
                                    <Route
                                      path="/my-subscriptions"
                                      element={<Navigate to="/subscription?tab=creators" replace />}
                                    />
                                    <Route
                                      path="/subscription"
                                      element={
                                        <ProtectedRoute>
                                          <React.Suspense
                                            fallback={
                                              <div className="p-token-md text-sm text-muted-foreground">Laden…</div>
                                            }
                                          >
                                            <MySubscription />
                                          </React.Suspense>
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/creator/:creatorId"
                                      element={
                                        <ProtectedRoute>
                                          <CreatorProfile />
                                        </ProtectedRoute>
                                      }
                                    />

                                    {/* Swipe Routes */}
                                    <Route
                                      path="/swipe"
                                      element={
                                        <ProtectedRoute>
                                          <Swipe />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/matches"
                                      element={
                                        <ProtectedRoute>
                                          <Matches />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/favorites"
                                      element={
                                        <ProtectedRoute>
                                          <Favorites />
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route path="/support" element={<Support />} />

                                    {/* Partner Billing (separate, nicht Admin) */}
                                    <Route
                                      path="/partner/billing"
                                      element={
                                        <ProtectedRoute requiredRole="partner_admin">
                                          <PartnerBillingDashboard />
                                        </ProtectedRoute>
                                      }
                                    />

                                    {/* Admin route registry is loaded only when entering the admin shell. */}
                                    <Route path="/admin/*" element={<AdminRouteMount />} />

                                    {/* 404 Catch-all route */}
                                    <Route path="*" element={<NotFound />} />
                                  </Routes>
                                </React.Suspense>
                              </RouteChrome>
                            </AdminSidebarProvider>
                          </OnboardingProvider>
                        </DOIProvider>
                      </WalletProvider>
                    </ToastProvider>
                  </NotificationProvider>
                </GlobalWebSocketProvider>
              </EditModeProvider>
            </AuthProvider>
          </DesignProvider>
        </ServiceWorkerProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

export default App;
